import { Image as AngelouImage } from "@newsela/angelou";
import PropTypes from "prop-types";

import { GradeBandKey } from "@/constants/GradeAndLexileLevels";
import { ContentLevel } from "@/types/models/ContentLevel.interface";
import convertToImgixUrl from "@/utils/convertToImgixUrl";
import findClosestGradeBand from "@/utils/findClosestGradeBand";
import getLanguageCode from "@/view/utils/getLanguageCode";
import { figureCss, captionCss, imageCss } from "./HeaderMediaImage.style";

const propTypes = {
  contentData: PropTypes.object,
  currentLevel: PropTypes.object,
};

export interface ContentData {
  /* incomplete representation of contentData */
  article_levels: ContentLevel[];
  header_image: {
    name?: string;
    url?: string;
    caption_levels?:
      | []
      | Array<{ grade_band: GradeBandKey; language: string; text: string }>;
  };
}

const HeaderMediaImage = ({
  contentData,
  currentLevel,
}: {
  contentData: ContentData;
  currentLevel: ContentLevel;
}) => {
  const headerImage = contentData?.header_image;
  const { name = "", url } = headerImage;

  if (!url) {
    return <></>;
  }

  const imgixUrl = convertToImgixUrl(url);

  /**
   * Get the best caption match; accounts for cases where a direct match between content level and
   * caption level is not available.
   **/
  const getBestCaption = () => {
    const currentGradeBand = currentLevel?.grade_band;
    const possibleCaptionLevels = headerImage?.caption_levels?.map(
      (captionLevel) => captionLevel.grade_band,
    );

    if (!possibleCaptionLevels) {
      return "";
    }
    const closestCaptionGradeBand = findClosestGradeBand(
      currentGradeBand,
      possibleCaptionLevels,
    );
    const selectedCaptionLevel = headerImage?.caption_levels?.find(
      (level) =>
        level.grade_band === closestCaptionGradeBand &&
        level.language === currentLevel?.language,
    );
    return selectedCaptionLevel?.text;
  };

  const bestCaption = getBestCaption();

  return (
    <figure css={figureCss}>
      <AngelouImage alt={name} src={imgixUrl} __css={imageCss} />
      {bestCaption ? (
        <figcaption
          lang={getLanguageCode(currentLevel.language)}
          css={captionCss}
        >
          {bestCaption}
        </figcaption>
      ) : (
        <></>
      )}
    </figure>
  );
};

HeaderMediaImage.propTypes = propTypes;

export default HeaderMediaImage;
